var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ContactMethod } from "healthex-common/consentconfig.dtos";
import { DocumentRetrievalLocationStatus, DocumentRetrievalTaskStatus } from "healthex-common/document.retrieval.dtos";
import { computed, observable } from "mobx";
import { getObservableT } from "../Infra/i18next";
import { NOT_LOADED } from "../Infra/RemoteData";
import { PatientConsentSessionState } from "./PatientConsentSession";
import { PatientConsentViewModel } from "./PatientConsentViewModel";
import { SimpleDialogModel } from "./SimpleDialogModel";
const t = getObservableT("patients");
export var ContinueAnywayChoice;
(function (ContinueAnywayChoice) {
    ContinueAnywayChoice["CONTINUE"] = "CONTINUE";
    ContinueAnywayChoice["ADD_HEALTHCARE_PROVIDER"] = "ADD_HEALTHCARE_PROVIDER";
    ContinueAnywayChoice["DO_NOTHING"] = "DO_NOTHING";
})(ContinueAnywayChoice || (ContinueAnywayChoice = {}));
export class PatientConsentRecordLocationViewModel extends PatientConsentViewModel {
    constructor(container) {
        super(container);
        this.finalizationWaitingStartTime = null;
        this.continueAnywayDialogModel = new SimpleDialogModel();
        this.now = null;
        this.showFastenPreview = false;
        this.documentRetrievalManager = container.documentRetrievalManager;
    }
    run() {
        const patientInfoEquals = (a, b) => {
            if (a === b)
                return true;
            if (a === null || b === null)
                return false;
            return a.orgId === b.orgId && a.patientId === b.patientId && a.studyId === b.studyId;
        };
        this.disposer.reaction(() => this.patientInfo, async () => {
            if (this.isScreenshotMode)
                return;
            if (!this.patientInfo)
                return;
            if (this.patientConsentUIManager.pendingRetrieval)
                return;
            if (this.patientConsentUIManager.session.patientConsentSessionState !== PatientConsentSessionState.INITIALIZED)
                return;
            await this.patientConsentUIManager.initiateDocumentRetrieval(this.patientInfo.studyId);
        }, {
            fireImmediately: true,
            equals: patientInfoEquals,
            name: "DocumentRetrievalInitiation",
        });
        this.disposer.reaction(() => [this.retrieval, this.patientConsentUIManager.session.hasAuthorizedConsentEnrollment], async ([retrieval, hasAuthorizedConsentEnrollment]) => {
            var _a;
            if (this.isScreenshotMode)
                return;
            if (!retrieval)
                return;
            if (this.isIAS && retrieval.documentSelectionComplete && !retrieval.finalized) {
                this.startFinalizationWaitingTimer();
            }
            else {
                this.clearFinalizationWaitingTimer();
            }
            let readyForConfirmation;
            if (this.isIAS) {
                readyForConfirmation = (retrieval === null || retrieval === void 0 ? void 0 : retrieval.finalized) || this.finalizationWaitingTimeExceeded;
            }
            else if (this.isAIFlow) {
                readyForConfirmation = retrieval === null || retrieval === void 0 ? void 0 : retrieval.documentSelectionComplete;
            }
            else {
                readyForConfirmation = retrieval === null || retrieval === void 0 ? void 0 : retrieval.documentSelectionComplete;
            }
            if (readyForConfirmation && (!!hasAuthorizedConsentEnrollment || this.isDataUpdate)) {
                const needsRecordSummary = this.isIAS &&
                    retrieval.documents.some((document) => document.status === DocumentRetrievalTaskStatus.COMPLETE) &&
                    !this.finalizationWaitingTimeExceeded;
                if (needsRecordSummary && !retrieval.patientData)
                    return;
                const contactMethod = this.preferredContactMethod !== NOT_LOADED ? this.preferredContactMethod : null;
                this.patientConsentUIManager.updateContactMethod(contactMethod, contactMethod === ContactMethod.EMAIL ? this.patientEmail : null, contactMethod === ContactMethod.TEXT ? this.patientPhone : null);
                if (this.isIAS && this.patientConsentUIManager.currentPage !== "ENROLLMENT_PDDA_HUR_LOADING") {
                    return;
                }
                if (((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.batchId) !== retrieval.batchId) {
                    return;
                }
                this.goToNextPage();
            }
        }, { fireImmediately: true, name: "DocumentRetrievalCompletion" });
    }
    stop() {
        this.clearFinalizationWaitingTimer();
        this.disposer.dispose();
    }
    startFinalizationWaitingTimer() {
        if (this.finalizationWaitingStartTime !== null)
            return;
        const now = new Date();
        this.finalizationWaitingStartTime = now;
        this.now = now;
        this.finalizationWaitingInterval = setInterval(() => {
            this.updateTime();
        }, 500);
    }
    clearFinalizationWaitingTimer() {
        clearInterval(this.finalizationWaitingInterval);
        this.finalizationWaitingStartTime = null;
    }
    updateTime() {
        this.now = new Date();
    }
    get finalizationWaitingTimeExceeded() {
        if (this.finalizationWaitingStartTime === null)
            return false;
        if (!this.now)
            return false;
        return this.now.getTime() - this.finalizationWaitingStartTime.getTime() > this.finalizationWaitingTimeAtMax * 1000;
    }
    get finalizationWaitingTimeAtMax() {
        return 10 * 60;
    }
    get patientInfo() {
        if (this.isPreview)
            return null;
        if (this.patientConsentUIManager.consentingPatientInfo === null)
            return null;
        if (this.authManager.patientConsentOrgId === null)
            return null;
        if (this.isIAS) {
            return {
                orgId: this.authManager.patientConsentOrgId,
                patientId: this.patientConsentUIManager.consentingPatientInfo.id,
                studyId: null,
            };
        }
        if (this.study === NOT_LOADED || this.study === null)
            return null;
        return {
            orgId: this.authManager.patientConsentOrgId,
            patientId: this.patientConsentUIManager.consentingPatientInfo.id,
            studyId: this.study.id,
        };
    }
    get isPreview() {
        return this.patientConsentUIManager.isInteractivePreview || this.patientConsentUIManager.isScreenshotPreviewMode;
    }
    get isLoading() {
        if (this.isPreview)
            return false;
        if (this.patientConsentUIManager.isScreenshotMode)
            return false;
        return this.patientInfo === null;
    }
    get isLocatingRecords() {
        if (this.isPreview)
            return false;
        if (this.isScreenshotMode)
            return false;
        if (!this.retrieval)
            return true;
        if (this.retrieval.tefcaQueries.some((query) => !query.isComplete))
            return true;
        return false;
    }
    get isContinueButtonEnabled() {
        if (this.isPreview)
            return true;
        if (this.isScreenshotMode)
            return true;
        if (!this.retrieval)
            return false;
        if (this.retrieval.tefcaQueries.some((query) => !query.isComplete))
            return false;
        const locationsNotStarted = this.retrieval.locations.some((location) => {
            return location.status === DocumentRetrievalLocationStatus.NEEDS_AUTH;
        });
        return !locationsNotStarted;
    }
    get continueText() {
        if (this.zeroLocationsFound) {
            if (!this.canAddLocations) {
                return t("Proceed to last step");
            }
            return t("Continue without retrieving records");
        }
        return t("Continue");
    }
    async continue() {
        if (this.isPreview) {
            this.goToNextPage();
            return;
        }
        if (!this.retrieval)
            return;
        const studyId = this.study !== NOT_LOADED && this.study !== null ? this.study.id : null;
        await this.documentRetrievalManager.doneSelectingDocuments(this.retrieval.batchId, studyId, true);
    }
    get previewMockedLocationData() {
        return this.patientConsentUIManager.previewMockedLocationData;
    }
}
__decorate([
    observable
], PatientConsentRecordLocationViewModel.prototype, "now", void 0);
__decorate([
    observable
], PatientConsentRecordLocationViewModel.prototype, "showFastenPreview", void 0);
__decorate([
    computed
], PatientConsentRecordLocationViewModel.prototype, "finalizationWaitingTimeExceeded", null);
__decorate([
    computed
], PatientConsentRecordLocationViewModel.prototype, "finalizationWaitingTimeAtMax", null);
__decorate([
    computed
], PatientConsentRecordLocationViewModel.prototype, "patientInfo", null);
__decorate([
    computed
], PatientConsentRecordLocationViewModel.prototype, "isPreview", null);
__decorate([
    computed
], PatientConsentRecordLocationViewModel.prototype, "isLoading", null);
__decorate([
    computed
], PatientConsentRecordLocationViewModel.prototype, "isLocatingRecords", null);
__decorate([
    computed
], PatientConsentRecordLocationViewModel.prototype, "isContinueButtonEnabled", null);
__decorate([
    computed
], PatientConsentRecordLocationViewModel.prototype, "continueText", null);
